/* eslint-disable max-len */
import {
    Box,
    Button, ListItem, ModalBody, ModalFooter, SimpleGrid, Spacer,
    Stack,
    Text,
    UnorderedList,
    useToast,
    VStack,
} from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { format } from 'date-fns';
import { KeyValuePair, ThemeTokens, Alert } from '@acp/core-ui';
import { TenancyStep } from '../tenancy-steps';
import { OccupantCard } from '../../../occupant/components/occupant-card';
import { FormType, FormValues } from '../schemas';
import { useAddTenancy, useEditTenancy } from '../../hooks/use-tenancy';

export function ConfirmationScreen({
    onSetScreen,
    onClose,
}: {
    onSetScreen: (screen: TenancyStep) => void;
    onClose: () => void;
}) {
    const toast = useToast();

    const { getValues, reset, handleSubmit } = useFormContext<FormValues>();
    const values = getValues();
    const addTenancyMutation = useAddTenancy(values.propertyId, {
        reset,
        onClose,
        onError: () => {
            toast({
                title: 'Failed to add tenancy',
                description: 'An error occurred while adding the tenancy',
                status: 'error',
                isClosable: true,
            });
        },
    });

    const editTenancyMutation = useEditTenancy(
        values.propertyId,
        values.tenancy.tenancyId!,
        {
            reset,
            onClose,
            onError: () => {
                toast({
                    title: 'Failed to update tenancy',
                    description: 'An error occurred while updating the tenancy',
                    status: 'error',
                    isClosable: true,
                });
            },
        }
    );

    const handleTenancyFormSubmit = (_data: FormValues) => {
        if (_data.tenancy.formType === FormType.Add) {
            addTenancyMutation.add(_data);
        }

        if (_data.tenancy.formType === FormType.Edit) {
            editTenancyMutation.edit(_data);
        }
    };

    return (
        <>
            <ModalBody minH="lg" px={ThemeTokens.TenancyModal.body.py}>
                <VStack align="left" spacing={10}>
                    <Stack align="left" spacing={6}>
                        <Text fontSize="lg" fontWeight="bold">Lease agreement details</Text>
                        <SimpleGrid columns={[1, 2]} spacing={6}>
                            <Box>
                                <KeyValuePair label="Tenancy start date">
                                    {
                                        values.tenancy.startDate !== null
                                            ? <Text>{format(values.tenancy.startDate, 'do MMMM yyyy')}</Text>
                                            : <Text color="muted.300">Not specified</Text>
                                    }
                                </KeyValuePair>
                            </Box>
                            <Box>
                                <KeyValuePair label="Tenancy end date">
                                    {
                                        values.tenancy.endDate !== null
                                            ? <Text>{format(values.tenancy.endDate, 'do MMMM yyyy')}</Text>
                                            : <Text color="muted.300">Not specified</Text>
                                    }
                                </KeyValuePair>
                            </Box>
                            <Box>
                                <KeyValuePair label="Renewal date">
                                    {
                                        values.tenancy.renewalDate !== null && values.tenancy.renewalDate
                                            ? <Text>{format(values.tenancy.renewalDate, 'do MMMM yyyy')}</Text>
                                            : <Text color="muted.300">Not specified</Text>
                                    }
                                </KeyValuePair>
                            </Box>
                        </SimpleGrid>
                        <Box>
                            <KeyValuePair label="Vacate date">
                                {
                                    values.tenancy.vacateDate !== null
                                        ? <Text>{format(values.tenancy.vacateDate, 'do MMMM yyyy')}</Text>
                                        : <Text color="muted.300">Not specified</Text>
                                }
                            </KeyValuePair>
                        </Box>
                        <Box>
                            <KeyValuePair label="Notes">
                                {
                                    values.tenancy.notes !== ''
                                        ? <Text>{values.tenancy.notes}</Text>
                                        : <Text color="muted.300">None</Text>
                                }
                            </KeyValuePair>
                        </Box>
                    </Stack>
                    <VStack align="left" spacing={6}>
                        <Text fontSize="lg" fontWeight="bold">Occupants</Text>
                        <VStack align="left" spacing={6}>
                            {
                                values.occupants.length > 0
                                    ? values.occupants.map((occupant) => (
                                        <OccupantCard {...occupant} key={occupant.name} />
                                    )) : (
                                        <Text color="muted.300">
                                            None
                                        </Text>
                                    )
                            }
                        </VStack>
                    </VStack>
                </VStack>
                {
                    addTenancyMutation.error !== null && (
                        <Alert title="Failed to add tenancy" status="error">
                            {
                                Array.isArray(addTenancyMutation.error.message)
                                    ? (
                                        <UnorderedList>
                                            {
                                                addTenancyMutation.error.message.map(
                                                    (message) => <ListItem>{message}</ListItem>
                                                )
                                            }
                                        </UnorderedList>
                                    )
                                    : addTenancyMutation.error.message
                            }
                        </Alert>
                    )
                }
                {
                    editTenancyMutation.error !== null && (
                        <Alert title="Failed to update tenancy" status="error">
                            {
                                Array.isArray(editTenancyMutation.error.message)
                                    ? (
                                        <UnorderedList>
                                            {
                                                editTenancyMutation.error.message.map(
                                                    (message) => <ListItem>{message}</ListItem>
                                                )
                                            }
                                        </UnorderedList>
                                    )
                                    : editTenancyMutation.error.message
                            }
                        </Alert>
                    )
                }
            </ModalBody>
            <ModalFooter>
                <Button
                    size="md"
                    colorScheme="muted"
                    variant="ghost"
                    onClick={() => onSetScreen(TenancyStep.Occupants)}
                >
                    Back
                </Button>
                <Spacer />
                <Button
                    colorScheme="secondary"
                    variant="solid"
                    size="md"
                    isLoading={addTenancyMutation.isPending || editTenancyMutation.isPending}
                    onClick={handleSubmit(handleTenancyFormSubmit)}
                >
                    {values.tenancy.formType === FormType.Add && 'Add tenancy'}
                    {values.tenancy.formType === FormType.Edit && 'Update tenancy'}
                </Button>
            </ModalFooter>
        </>
    );
}
